import type { PropsWithChildren } from 'react';
import React, { createContext, useMemo } from 'react';

import type { Logger } from '@feature-hub/logger';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { AudiVideoServiceInterfaceV1 } from '@oneaudi/video-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';

interface FeatureAppEnv {
  featureAppId: string;
  featureAppName?: string;
}

interface ContextState {
  readonly featureAppEnv: FeatureAppEnv;
  readonly loggerService?: Logger;
  readonly trackingService?: TrackingServiceV2;
  readonly videoService?: AudiVideoServiceInterfaceV1;
  readonly localeService: GfaLocaleServiceV1;
}

export const Context = createContext<ContextState>({} as ContextState);

export interface ContextProviderProps {
  readonly featureAppEnv: FeatureAppEnv;
  readonly loggerService?: Logger;
  readonly trackingService?: TrackingServiceV2;
  readonly videoService?: AudiVideoServiceInterfaceV1;
  readonly localeService: GfaLocaleServiceV1;
}

const ContextProvider = ({
  children,
  featureAppEnv,
  loggerService: logger,
  trackingService,
  videoService,
  localeService,
}: PropsWithChildren<ContextProviderProps>) => {
  const services = useMemo(() => {
    return {
      featureAppEnv,
      loggerService: logger,
      trackingService,
      videoService,
      localeService,
    };
  }, [featureAppEnv, trackingService, videoService, logger, localeService]);
  return <Context.Provider value={services}>{children}</Context.Provider>;
};
export default ContextProvider;
